import {
  Alert,
  Box,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import type {
  PlaygroundModel,
  PlaygroundRunSummary,
} from '@api/demand-playground';
import { statusText } from './RunDetail';
import { formatScore, modelLabel } from '../utils/playground';

interface Props {
  runs: PlaygroundRunSummary[] | undefined;
  selectedId: number | null;
  onOpen: (id: number) => void;
  models?: PlaygroundModel[];
  error?: unknown;
}

const time = (iso: string) => {
  const value = Date.parse(iso);
  return Number.isFinite(value) ? value : 0;
};

/** Newest first, whatever order they came in. */
export const newestFirst = (runs: PlaygroundRunSummary[]) =>
  [...runs].sort((a, b) => time(b.created_at) - time(a.created_at));

const when = (iso: string) => {
  const value = Date.parse(iso);
  return Number.isFinite(value)
    ? new Date(value).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short',
      })
    : '';
};

/** Saved runs, newest first (ZLAYT-1660). */
function RunsList({ runs, selectedId, onOpen, models, error }: Props) {
  let empty = null;
  if (!runs && error) {
    empty = <Alert severity="error">The saved runs could not be loaded.</Alert>;
  } else if (!runs) {
    empty = <CircularProgress size={18} />;
  } else if (!runs.length) {
    empty = (
      <Typography variant="body2" sx={{ color: '#475467' }}>
        No runs yet.
      </Typography>
    );
  }
  return (
    <Box>
      <Typography variant="h4" fontWeight={500} sx={{ mb: 1 }}>
        Saved runs
      </Typography>
      {empty || !runs ? (
        empty
      ) : (
        <TableContainer sx={{ maxHeight: 420, overflowX: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Test case</TableCell>
                <TableCell>AI model</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Started</TableCell>
                <TableCell align="right">New score</TableCell>
                <TableCell align="right">Current score</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {newestFirst(runs).map((run) => (
                <TableRow key={run.id} hover selected={run.id === selectedId}>
                  <TableCell>{run.name || `Run ${run.id}`}</TableCell>
                  <TableCell>{run.case_label}</TableCell>
                  <TableCell>{modelLabel(run.writer_model, models)}</TableCell>
                  <TableCell>{statusText(run.status)}</TableCell>
                  <TableCell>{when(run.created_at)}</TableCell>
                  <TableCell align="right">
                    {formatScore(run.new_score)}
                  </TableCell>
                  <TableCell align="right">
                    {formatScore(run.current_score)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      aria-label={`Open ${run.name || `Run ${run.id}`}`}
                      onClick={() => onOpen(run.id)}
                    >
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}

export default RunsList;
